import { Component, DestroyRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatFormField } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatAnchor } from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { NuevoPaciente } from './nuevo-paciente/nuevo-paciente';
import { RegistroSignosVitales } from './registro-signos-vitales/registro-signos-vitales';
import { PacienteService } from '../../core/services/pacientes.service';
import { GraficasSignosVitales } from './registro-signos-vitales/graficas-signos-vitales/graficas-signos-vitales';
import { MatTooltipModule } from '@angular/material/tooltip';
import Swal from 'sweetalert2';
import { InformacionPaciente } from './informacion-pacientes/informacion-pacientes';
import { AuthService } from '../../core/services/auth.service';
import { ResumenRecetaDialog } from '../estudios/resumen-receta-dialog/resumen-receta-dialog';
import { debounceTime, Subscription, tap, merge } from 'rxjs';
import { SocketService } from '../../core/services/shoket.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

export type EstadoDashboard =
  | 'SOLO_SIGNOS'
  | 'EN_ESPERA'
  | 'EN_CONSULTA'
  | 'FINALIZADA'
  | 'SOLO_REGISTRO'
  | 'CANCELADO';

export interface AtencionDashboard extends Atencion {}

export interface SignosVitales {
  id: string;
  peso: number | null;
  estatura: number | null;
  saturacionOxigeno: number | null;
  frecuenciaCardiaca: number | null;
  frecuenciaRespiratoria: number | null;
  glucosa: number | null;
  temperatura: number | null;
  presionSistolica: number | null;
  presionDiastolica: number | null;
  observaciones: string | null;
  fechaRegistro: Date | string;
}

export interface Paciente {
  id: string;
  nombre: string;
  apellido: string | null;
  segundoApellido: string | null;
  fechaNacimiento: Date | string | null;
  genero: string | null;
  tipoSangre: string | null;

  municipio?: CampoCatalogo | null;
  barrio?: CampoCatalogo | null;

  direccion: string | null;
  correo: string | null;
  telefono: string | null;
  nombreContactoEmergencia: string | null;
  parentescoContactoEmergencia: string | null;
  direccionContactoEmergencia: string | null;
  telefonoContactoEmergencia: string | null;
  correoContactoEmergencia: string | null;
  numeroExpedienteMedico?: string | null;
  expediente?: Expediente;
}

export interface CampoCatalogo {
  tipo: string;
  valor: string;
}

export interface Expediente {
  vacunas?: string | null;
  viajesRecientes?: string | null;
  lugarOrigen?: string | null;
  alimentacion?: string | null;
  agua?: string | null;
  vivienda?: string | null;
  alergias?: string | null;
  transfucionesSangre?: string | null;
  cirugiasPrevias?: string | null;
  traumasFracturasPrev?: string | null;
  enfermedadesConcomitantes?: string | null;
  diabetes?: string | null;
  hipertension?: string | null;
  asma?: string | null;
  epoc?: string | null;
  antecedenteCancer?: string | null;
  antecedentesTuberculosis?: string | null;
}

export interface ArchivoAtencion {
  id: string;
  hallazgos: string;
}

export interface Atencion {
  archivos?: ArchivoAtencion[];
  id: string;
  paciente?: Paciente;
  medico: any | null;
  estado: 'SOLO_SIGNOS' | 'EN_ESPERA' | 'EN_ATENCION' | 'ATENDIDO' | 'CANCELADO';
  prioridad: string;
  padecimiento: string | null;
  hallazgos: string | null;
  impresion: string | null;
  horaLlegada: Date | string;
  horaInicio: Date | string | null;
  horaFin: Date | string | null;
  signosVitales: SignosVitales;
  receta: {
    id: number;
    folio: string;
  } | null;
}

export interface AnalisisClinico {
  id?: string;
  estudio: string;
  archivo?: File | null;
  archivoNombre?: string | null;
  resumen: string;
  fecha: Date | string;
  editable?: boolean;
}

@Component({
  selector: 'app-pacientes',
  imports: [
    FormsModule,
    MatIcon,
    MatFormField,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatAnchor,
    MatButtonModule,
    MatTableModule,
    MatTooltipModule,
    MatSnackBarModule,
  ],
  templateUrl: './pacientes.html',
  styleUrl: './pacientes.scss',
})
export class Pacientes {
  private socket = inject(SocketService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private pacienteService: PacienteService,
    private snackBar: MatSnackBar,
  ) {}

  columnas: string[] = ['paciente', 'informacion', 'editar', 'signos', 'recetas'];

  limpiarBusqueda(input?: HTMLInputElement): void {
    this.busqueda = '';
    this.filtrarPacientes();
    input?.focus(); // deja el cursor listo para escribir otro nombre
  }

  tieneRol(rol: string): boolean {
    return this.user?.rol?.nombre === rol;
  }
  // ==========================================
  // COLUMNAS SEGÚN EL ROL
  // ==========================================

  actualizarColumnas(): void {
    this.columnas = ['paciente', 'informacion', 'editar', 'signos', 'recetas'];

    if (this.user?.rol?.nombre === 'MEDIC@' || this.user?.rol?.nombre === 'ADMIN') {
      this.columnas.push('eliminar');
    }
  }

  // Tabla pacientes

  pacientes = new MatTableDataSource<Paciente>();

  // <!-- BUSCAR PACIENTES -->
  busqueda: string = '';

  filtrarPacientes(): void {
    this.pacientes.filter = this.busqueda.trim().toLowerCase();
  }
  // <!-- NUEVO PACIENTE -->

  user: any;
  ngOnInit() {
    this.user = this.authService.getUser();
    this.actualizarColumnas();
    // this.socket.connect(this.authService.getToken()!);

    this.pacientes.filterPredicate = (paciente: Paciente, filtro: string) => {
      const texto = filtro.toLowerCase().trim();

      const nombreCompleto = `
        ${paciente.nombre ?? ''}
        ${paciente.apellido ?? ''}
        ${paciente.segundoApellido ?? ''}
      `.toLowerCase();

      const telefono = paciente.telefono?.toLowerCase() ?? '';
      const correo = paciente.correo?.toLowerCase() ?? '';

      return nombreCompleto.includes(texto) || telefono.includes(texto) || correo.includes(texto);
    };

    this.cargarPacientes();
    this.escucharEventosWs();
  }

  private escucharEventosWs(): void {
    const pacienteCreado$ = this.socket.on<any>('paciente.creado').pipe(
      tap((data) => {
        const paciente = data?.paciente ?? data;

        const nombre = [paciente?.nombre, paciente?.apellido, paciente?.segundoApellido]
          .filter(Boolean)
          .join(' ');

        this.snackBar.open(
          nombre ? `Nuevo paciente registrado: ${nombre}` : 'Se ha registrado un nuevo paciente.',
          'Cerrar',
          {
            duration: 12000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['notificacion-novamed'],
          },
        );
      }),
    );

    const pacienteActualizado$ = this.socket.on<any>('paciente.actualizado');

    const pacienteEliminado$ = this.socket.on<any>('paciente.eliminado');

    merge(pacienteCreado$, pacienteActualizado$, pacienteEliminado$)
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cargarPacientes());
  }

  abrirNuevoPaciente() {
    const dialogRef = this.dialog.open(NuevoPaciente, {
      width: '95vw',
      height: '90vh',
      maxWidth: '95vw',
      data: {
        modo: 'nuevo',
      },
    });
    dialogRef.afterClosed().subscribe((resultado) => {
      console.log('resultado nuevo paciente:', resultado);
      if (!resultado) {
        return;
      }

      this.cargarPacientes();

      if (resultado.abrirSignosVitales && resultado.paciente) {
        this.abrirRegistroSignosVitales(resultado.paciente);
      }
    });
  }

  abrirEditarPaciente(paciente: Paciente) {
    const dialogRef = this.dialog.open(NuevoPaciente, {
      width: '95vw',
      maxWidth: '95vw',
      height: '90vh',
      data: {
        modo: 'editar',
        paciente: paciente,
      },
    });

    dialogRef.afterClosed().subscribe((resultado) => {
      if (resultado) {
        // console.log('========== PACIENTE ACTUALIZADO ==========');
        console.log(resultado);
        this.cargarPacientes();
      }
    });
  }

  cargarPacientes() {
    this.pacienteService.obtenerPacientes().subscribe({
      next: (respuesta) => {
        // console.log('========== RESPUESTA DEL BACKEND ==========');
        // console.log(respuesta);
        // console.log('¿Es un arreglo?', Array.isArray(respuesta));
        // console.log('Cantidad de pacientes:', respuesta?.length);
        // console.log('========== RESPUESTA DEL BACKEND ==========');
        // console.log(respuesta);

        // console.log('========== PRIMER PACIENTE ==========');
        // console.log(respuesta[0]);

        // console.log('========== CAMPOS DEL PRIMER PACIENTE ==========');
        // console.log('nombre:', respuesta[0]?.nombre);
        // console.log('apellido:', respuesta[0]?.apellido);
        // console.log('apellidoPaterno:', respuesta[0]?.apellidoPaterno);
        // console.log('segundoApellido:', respuesta[0]?.segundoApellido);

        this.pacientes.data = respuesta.sort((a, b) => {
          const nombreA = `${a.nombre ?? ''} ${a.apellido ?? ''} ${a.segundoApellido ?? ''}`.trim();

          const nombreB = `${b.nombre ?? ''} ${b.apellido ?? ''} ${b.segundoApellido ?? ''}`.trim();

          return nombreA.localeCompare(nombreB, 'es', {
            sensitivity: 'base',
          });
        });

        // console.log('========== DATOS DE LA TABLA ==========');
        // console.log(this.pacientes.data);
      },

      error: (error) => {
        // console.error('Error al cargar pacientes:', error);
      },
    });
  }

  abrirInformacionPaciente(paciente: Paciente) {
    this.dialog.open(InformacionPaciente, {
      width: '800px',
      maxWidth: '95vw',
      height: '85vh',
      data: paciente,
    });
  }

  abrirRegistroSignosVitales(paciente: Paciente) {
    this.dialog.open(RegistroSignosVitales, {
      width: '95vw',
      maxWidth: '95vw',
      height: '90vh',
      data: paciente,
    });
  }

  abrirGragicaSignosVitales(paciente: Paciente) {
    this.dialog.open(GraficasSignosVitales, {
      width: '95vw',
      height: '90vh',
      maxWidth: '95vw',
      data: paciente,
    });
  }

  eliminarPaciente(paciente: Paciente): void {
    if (this.user?.rol?.nombre === 'ENFERMER@') {
      return;
    }

    Swal.fire({
      title: '¿Eliminar paciente?',
      text: `¿Estás seguro de eliminar a ${paciente.nombre} ${paciente.apellido ?? ''}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (resultado.isConfirmed) {
        if (!paciente.id) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'El paciente no tiene un ID válido.',
            showConfirmButton: false,
            timer: 2000,
          });

          return;
        }

        this.pacienteService.eliminarPaciente(paciente.id).subscribe({
          next: () => {
            Swal.fire({
              icon: 'success',
              title: 'Paciente eliminado',
              text: 'El paciente se eliminó correctamente.',
              showConfirmButton: false,
              timer: 2000,
            });

            this.cargarPacientes();
          },

          error: (error) => {
            // console.error('Error al eliminar paciente:', error);

            Swal.fire({
              icon: 'error',
              title: 'No se pudo eliminar',
              text: 'Ocurrió un error al eliminar el paciente.',
              showConfirmButton: false,
              timer: 2000,
            });
          },
        });
      }
    });
  }

  abrirResumenReceta(paciente: Paciente): void {
    this.dialog.open(ResumenRecetaDialog, {
      width: '700px',
      maxWidth: '95vw',
      height: '80vh',
      data: paciente,
    });
  }
}
